/**
 * Scene 1: The Opening
 * "₦XX Trillion Distributed"
 */

import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AnimatedCounter } from '../AnimatedCounter';
import { NigeriaMap } from '../NigeriaMap';
import { NairaIcon } from '../../icons/StoryIcons';
import type { StateGeoFeature } from '../../../types';
import type { StoryInsights } from '../../../hooks/useStoryData';

gsap.registerPlugin(ScrollTrigger);

interface Scene1Props {
  insights: StoryInsights;
  features: StateGeoFeature[];
  isActive: boolean;
}

export function Scene1Opening({ insights, features, isActive }: Scene1Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const counterRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<SVGSVGElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const nairaIconsRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const ctx = gsap.context(() => {
      // Only set initial hidden state if not active
      if (!isActive) {
        gsap.set([titleRef.current, counterRef.current, subtitleRef.current], {
          opacity: 0,
          y: 30,
        });
        
        // Floating naira icons
        if (nairaIconsRef.current) {
          const icons = nairaIconsRef.current.querySelectorAll('.floating-naira');
          gsap.set(icons, {
            opacity: 0,
            scale: 0,
            rotation: () => gsap.utils.random(-180, 180),
          });
        }
      }
      
      // Scroll trigger animation
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top center',
          end: 'center center',
          toggleActions: 'play none none reverse',
        },
      });
      
      tl.to(titleRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      })
      .to(counterRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      }, '-=0.4')
      .to(subtitleRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      }, '-=0.4');
      
      // Floating nairas animation
      if (nairaIconsRef.current) {
        const icons = nairaIconsRef.current.querySelectorAll('.floating-naira');
        icons.forEach((icon, i) => {
          gsap.to(icon, {
            opacity: 0.3,
            scale: 1,
            rotation: 0,
            duration: 1,
            delay: 0.5 + i * 0.1,
            ease: 'back.out(1.7)',
          });
          
          // Continuous floating
          gsap.to(icon, {
            y: '+=20',
            duration: 2 + Math.random(),
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
            delay: Math.random() * 2,
          });
        });
      }
    }, containerRef);
    
    return () => ctx.revert();
  }, []);
  
  // Force visibility when scene becomes active (for auto-scroll)
  useEffect(() => {
    if (isActive) {
      gsap.to([titleRef.current, counterRef.current, subtitleRef.current], {
        opacity: 1,
        y: 0,
        duration: 0.5,
        stagger: 0.1,
      });
      
      if (nairaIconsRef.current) {
        const icons = nairaIconsRef.current.querySelectorAll('.floating-naira');
        gsap.to(icons, { opacity: 0.3, scale: 1, duration: 0.5 });
      }
    }
  }, [isActive]);
  
  // Generate random positions for floating naira icons
  const nairaPositions = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    left: `${5 + (i % 6) * 15 + Math.random() * 10}%`,
    top: `${10 + Math.floor(i / 6) * 40 + Math.random() * 20}%`,
    size: 20 + Math.random() * 20,
  }));
  
  return (
    <section
      ref={containerRef}
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-4 py-20"
    >
      {/* Floating Naira Icons Background */}
      <div ref={nairaIconsRef} className="absolute inset-0 pointer-events-none">
        {nairaPositions.map(({ id, left, top, size }) => (
          <NairaIcon
            key={id}
            className="floating-naira absolute text-accent-500/20"
            style={{ left, top }}
            size={size}
          />
        ))}
      </div>
      
      {/* Background Map */}
      <div className="absolute inset-0 flex items-center justify-center opacity-10">
        <NigeriaMap
          ref={mapRef}
          features={features}
          width={600}
          height={600}
          defaultColor="#06b6d4"
          animate={isActive}
        />
      </div>
      
      {/* Content */}
      <div className="relative z-10 text-center max-w-4xl">
        <h2
          ref={titleRef}
          className="text-lg md:text-xl font-medium text-surface-400 mb-4 tracking-wide uppercase"
        >
          Following the Money
        </h2>
        
        <div ref={counterRef} className="mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-white">
              ₦
            </span>
            <AnimatedCounter
              target={insights.totalAllocation}
              duration={3}
              formatAsTrillions
              decimals={2}
              className="text-5xl md:text-7xl lg:text-8xl font-display font-bold text-gradient"
              animate={isActive}
            />
          </div>
          <span className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-accent-400">
            Trillion
          </span>
        </div>
        
        <p
          ref={subtitleRef}
          className="text-xl md:text-2xl text-surface-300 leading-relaxed max-w-2xl mx-auto"
        >
          Since <span className="text-white font-semibold transition-all duration-300 hover:text-accent-300 cursor-default">{insights.yearsSpan.start}</span>, 
          the Federation Account Allocation Committee has distributed over{' '}
          <span className="text-accent-400 font-semibold transition-all duration-300 hover:text-accent-300 hover:drop-shadow-[0_0_8px_rgba(6,182,212,0.5)] cursor-default">{insights.totalAllocationFormatted}</span>{' '}
          to Nigeria's <span className="text-white font-semibold transition-all duration-300 hover:text-accent-300 cursor-default">{insights.totalStates}</span> states and FCT.
        </p>
        
        {/* Key stats */}
        <div className="mt-12 grid grid-cols-3 gap-8 max-w-xl mx-auto">
          <div className="text-center group cursor-default p-4 rounded-xl transition-all duration-300 hover:bg-surface-800/50 hover:scale-105">
            <div className="text-3xl md:text-4xl font-display font-bold text-white transition-all duration-300 group-hover:text-accent-400 group-hover:scale-110">
              {insights.yearsSpan.end - insights.yearsSpan.start + 1}
            </div>
            <div className="text-sm text-surface-400 uppercase tracking-wide transition-colors duration-300 group-hover:text-surface-300">Years</div>
          </div>
          <div className="text-center group cursor-default p-4 rounded-xl transition-all duration-300 hover:bg-surface-800/50 hover:scale-105">
            <div className="text-3xl md:text-4xl font-display font-bold text-white transition-all duration-300 group-hover:text-accent-400 group-hover:scale-110">
              {insights.totalStates}
            </div>
            <div className="text-sm text-surface-400 uppercase tracking-wide transition-colors duration-300 group-hover:text-surface-300">States + FCT</div>
          </div>
          <div className="text-center group cursor-default p-4 rounded-xl transition-all duration-300 hover:bg-surface-800/50 hover:scale-105">
            <div className="text-3xl md:text-4xl font-display font-bold text-white transition-all duration-300 group-hover:text-accent-400 group-hover:scale-110">
              {insights.yearlyTotals.length * 12}
            </div>
            <div className="text-sm text-surface-400 uppercase tracking-wide transition-colors duration-300 group-hover:text-surface-300">Months</div>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      {/* <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ScrollIndicator size={24} className="text-surface-500" />
      </div> */}
    </section>
  );
}

export default Scene1Opening;

